import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';

import { Shield, Users, FileText, TrendingUp, Calculator } from 'lucide-react';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import BackButton from '../components/BackButton';

import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function DispositifsBTPPage() {
  // Ajouter classe au body pour exclure l'arrière-plan
  React.useEffect(() => {
    document.body.classList.add('home-page');
    return () => {
      document.body.classList.remove('home-page');
    };
  }, []);

  const [formData, setFormData] = useState({
    nom: '',
    prenom: '',
    email: '',
    telephone: '',
    entreprise: '',
    message: ''
  });

  const _handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/demandes-publiques`, formData);
      toast.success('Votre demande a été envoyée avec succès !');
      setFormData({
        nom: '',
        prenom: '',
        email: '',
        telephone: '',
        entreprise: '',
        message: ''
      });
    } catch {
      toast.error('Erreur lors de l\'envoi de la demande');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-coppam-light-blue to-blue-50">
      
      {/* Bouton Retour */}
      <BackButton label="Retour" />

{/* Navigation */}
      <Navigation />

      {/* Hero Section amélioré */}
      <section className="relative w-full overflow-hidden" style={{ height: '650px', minHeight: '500px' }}>
        {/* Image de fond */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ 
            backgroundImage: 'url(/templates/page_template.svg)',
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        >
          {/* Overlay gradient moderne */}
          <div className="absolute inset-0 bg-gradient-to-br from-blue-900/70 via-slate-900/60 to-indigo-900/70" />
        </div>

        {/* Contenu Hero */}
        <div className="relative z-10 h-full flex items-center justify-center">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            {/* Badge */}
            <div className="inline-block mb-6">
              <div className="flex items-center gap-2 bg-blue-500/20 backdrop-blur-sm px-4 py-2 rounded-full border border-blue-400/30">
                <Shield className="h-4 w-4 text-blue-300" />
                <span className="text-blue-200 font-semibold text-sm uppercase tracking-wider">
                  Dispositifs O'SCOP
                </span>
              </div>
            </div>

            {/* Titre principal */}
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-4 leading-tight">
              Activités du BTP
            </h1>
            <p className="text-2xl sm:text-3xl font-semibold text-blue-300 mb-6">
              Protection Solidaire pour les Professionnels
            </p>

            {/* Description */}
            <p className="text-lg sm:text-xl text-gray-200 mb-10 max-w-3xl mx-auto leading-relaxed">
              Des dispositifs de sécurisation adaptés aux réalités du secteur de la construction, 
              basés sur l'entraide associative et l'économie sociale et solidaire.
            </p>

            {/* Boutons d'action */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link to="/login">
                <Button
                  size="lg"
                  className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700 text-white px-8 py-6 text-lg font-semibold rounded-lg shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-105"
                >
                  <Shield className="mr-2 h-5 w-5" />
                  Espace membre
                </Button>
              </Link>
              
              <Link to="/register">
                <Button
                  size="lg"
                  variant="outline"
                  className="w-full sm:w-auto bg-white/10 backdrop-blur-sm hover:bg-white/20 text-white border-white/30 px-8 py-6 text-lg font-semibold rounded-lg shadow-lg hover:shadow-xl transition-all duration-300"
                >
                  <Users className="mr-2 h-5 w-5" />
                  Devenir membre
                </Button>
              </Link>

              {/* V38.14 — Accès direct au simulateur CAP BATI+ CMI */}
              <Link to="/simulateurs/cap-bati-plus-cmi" data-testid="dispositifs-btp-cap-bati-link">
                <Button
                  size="lg"
                  className="w-full sm:w-auto bg-orange-600 hover:bg-orange-700 text-white px-8 py-6 text-lg font-semibold rounded-lg shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-105"
                >
                  <Calculator className="mr-2 h-5 w-5" />
                  Simulateur CAP BATI+ CMI
                </Button>
              </Link>
            </div>

            {/* Stats rapides */}
            <div className="mt-12 grid grid-cols-3 gap-6 max-w-2xl mx-auto">
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/20">
                <div className="text-3xl font-bold text-blue-300">500+</div>
                <div className="text-sm text-gray-300 mt-1">Membres Actifs</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/20">
                <div className="text-3xl font-bold text-blue-300">€2M+</div>
                <div className="text-sm text-gray-300 mt-1">Protections</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/20">
                <div className="text-3xl font-bold text-blue-300">15+</div>
                <div className="text-sm text-gray-300 mt-1">Ans d'Expérience</div>
              </div>
            </div>
          </div>
        </div>

        {/* Dégradé transition */}
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-white via-white/50 to-transparent" />
      </section>

      {/* Features */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white" id="dispositifs">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold text-center text-slate-900 mb-12">
            Nos Dispositifs
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
            <Card className="hover:shadow-lg transition-shadow border-coppam-blue/20">
              <CardHeader>
                <Shield className="h-12 w-12 text-coppam-blue mb-4" />
                <CardTitle className="text-coppam-blue">Gros Œuvre</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-slate-600">Protection adaptée aux entreprises de gros œuvre et construction.</p>
              </CardContent>
            </Card>
            
            <Card className="hover:shadow-lg transition-shadow border-coppam-gold/20">
              <CardHeader>
                <Users className="h-12 w-12 text-coppam-gold mb-4" />
                <CardTitle className="text-coppam-blue">Second Œuvre</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-slate-600">Couverture pour les professionnels du second œuvre.</p>
              </CardContent>
            </Card>
            
            <Card className="hover:shadow-lg transition-shadow border-coppam-blue/20">
              <CardHeader>
                <TrendingUp className="h-12 w-12 text-coppam-blue mb-4" />
                <CardTitle className="text-coppam-blue">Travaux Publics</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-slate-600">Dispositifs spécialisés pour les travaux publics.</p>
              </CardContent>
            </Card>
            
            <Card className="hover:shadow-lg transition-shadow border-coppam-gold/20">
              <CardHeader>
                <FileText className="h-12 w-12 text-coppam-gold mb-4" />
                <CardTitle className="text-coppam-blue">Professionnels Intellectuels</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-slate-600">Pour les architectes, bureaux d'études, etc.</p>
              </CardContent>
            </Card>

            <Card 
              className="hover:shadow-xl transition-all border-blue-600/40 bg-gradient-to-br from-blue-50 to-indigo-50 cursor-pointer"
              onClick={() => window.location.href = '/presentation-rcds'}
            >
              <CardHeader>
                <Shield className="h-12 w-12 text-blue-600 mb-4" />
                <CardTitle className="text-blue-900">Garantie RCDS</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-slate-700 font-medium mb-2">Responsabilité Civile & Décennale Solidaire</p>
                <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white mt-2">
                  En savoir plus →
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* FTPE Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-coppam-blue to-coppam-blue/90 text-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              Adhésion FTPE Outremer
            </h2>
            <p className="text-lg text-white/80 mb-6">
              Condition sine qua non pour bénéficier de nos dispositifs solidaires.
            </p>
            <p className="text-2xl font-bold mb-6 text-coppam-light-gold">200€ / an</p>
            <a href="https://ftpeguadeloupe.fr" target="_blank" rel="noopener noreferrer">
              <Button size="lg" className="bg-coppam-gold hover:bg-coppam-gold/90 text-white">
                En savoir plus sur la FTPE
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Section Juridique */}
      <section className="py-16 bg-slate-50 border-t border-slate-200">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-center text-coppam-blue mb-4">
              Documentation Juridique
            </h2>
            <p className="text-center text-slate-600 mb-10">
              Consultez nos documents juridiques et références légales
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Note sur le nom COPPAM */}
              <a 
                href="/juridique/note_nom_coppam_non_assurantiel.html" 
                className="group block p-6 bg-white rounded-lg border-2 border-slate-200 hover:border-coppam-gold transition-all duration-300 hover:shadow-lg"
              >
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-coppam-light-blue rounded-lg flex items-center justify-center">
                    <svg className="w-6 h-6 text-coppam-blue" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-coppam-blue group-hover:text-coppam-gold transition-colors mb-2">
                      Note sur le nom COPPAM
                    </h3>
                    <p className="text-sm text-slate-600">
                      Explication du caractère non assurantiel de COPPAM
                    </p>
                    <div className="flex gap-2 mt-3">
                      <span className="inline-flex items-center px-2 py-1 text-xs font-medium text-coppam-blue bg-coppam-light-blue rounded">
                        HTML
                      </span>
                      <a 
                        href="/juridique/note_nom_coppam_non_assurantiel.pdf"
                        className="inline-flex items-center px-2 py-1 text-xs font-medium text-red-700 bg-red-50 rounded hover:bg-red-100"
                        onClick={(e) => e.stopPropagation()}
                      >
                        PDF
                      </a>
                    </div>
                  </div>
                </div>
              </a>

              {/* Clause ACPR */}
              <a 
                href="/juridique/clause_ACPR.html"
                className="group block p-6 bg-white rounded-lg border-2 border-slate-200 hover:border-coppam-gold transition-all duration-300 hover:shadow-lg"
              >
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-coppam-light-blue rounded-lg flex items-center justify-center">
                    <svg className="w-6 h-6 text-coppam-blue" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-coppam-blue group-hover:text-coppam-gold transition-colors mb-2">
                      Clause ACPR
                    </h3>
                    <p className="text-sm text-slate-600">
                      Déclaration relative à l'Autorité de Contrôle Prudentiel et de Résolution
                    </p>
                    <span className="inline-flex items-center px-2 py-1 text-xs font-medium text-coppam-blue bg-coppam-light-blue rounded mt-3">
                      HTML
                    </span>
                  </div>
                </div>
              </a>

              {/* Jurisprudences */}
              <a 
                href="/juridique/jurisprudences.html"
                className="group block p-6 bg-white rounded-lg border-2 border-slate-200 hover:border-coppam-gold transition-all duration-300 hover:shadow-lg"
              >
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-coppam-light-blue rounded-lg flex items-center justify-center">
                    <svg className="w-6 h-6 text-coppam-blue" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-coppam-blue group-hover:text-coppam-gold transition-colors mb-2">
                      Jurisprudences
                    </h3>
                    <p className="text-sm text-slate-600">
                      Références jurisprudentielles et décisions de justice
                    </p>
                    <span className="inline-flex items-center px-2 py-1 text-xs font-medium text-coppam-blue bg-coppam-light-blue rounded mt-3">
                      HTML
                    </span>
                  </div>
                </div>
              </a>

              {/* Documents Légaux */}
              <a 
                href="/juridique/documents_legaux.html"
                className="group block p-6 bg-white rounded-lg border-2 border-slate-200 hover:border-coppam-gold transition-all duration-300 hover:shadow-lg"
              >
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-coppam-light-blue rounded-lg flex items-center justify-center">
                    <svg className="w-6 h-6 text-coppam-blue" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-coppam-blue group-hover:text-coppam-gold transition-colors mb-2">
                      Documents Légaux
                    </h3>
                    <p className="text-sm text-slate-600">
                      Ensemble des documents légaux et réglementaires
                    </p>
                    <span className="inline-flex items-center px-2 py-1 text-xs font-medium text-coppam-blue bg-coppam-light-blue rounded mt-3">
                      HTML
                    </span>
                  </div>
                </div>
              </a>
            </div>

            {/* Lien vers la page principale juridique */}
            <div className="text-center mt-10">
              <a 
                href="/juridique.html"
                className="inline-flex items-center gap-2 px-6 py-3 bg-coppam-blue text-white rounded-lg hover:bg-coppam-blue/90 transition-colors font-semibold"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Accéder à la section juridique complète
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* V37.57 LEGALCOOP — Forfait Revue de conformité CCMI */}
      <section className="py-16 bg-gradient-to-br from-indigo-50 via-slate-50 to-indigo-50" data-testid="dispositifs-legalcoop-section">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-slate-800 mb-2">
              LEGALCOOP — Revue de conformité CCMI
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Faites revoir votre Contrat de Construction de Maison Individuelle (CCMI) avec un appui documentaire.
            </p>
            {/* V37.60 — Definition officielle (source de verite) */}
            <div className="max-w-3xl mx-auto mt-6 bg-white rounded-xl border border-indigo-200 p-4">
              <p className="text-xs text-slate-700 leading-relaxed" data-testid="legalcoop-official-definition-vitrine">
                LEGALCOOP est un espace de revue documentaire d&apos;aide à la conformité CCMI,
                proposé dans le cadre du programme COPPAM hébergé par OBJECTIF SCOP OUTREMER
                SCIC SAS. Il permet au demandeur de transmettre un projet ou contrat CCMI,
                d&apos;obtenir une analyse documentaire assistée, puis une revue humaine par un
                juriste ou un intervenant habilité dans le workflow interne. Le service vise
                à identifier les points de vigilance, les pièces manquantes et les éléments
                à compléter avant validation humaine. Il ne constitue pas une consultation
                juridique formelle ni un avis juridique définitif.
              </p>
            </div>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="md:col-span-2 bg-white rounded-2xl shadow-md p-6 border border-indigo-100">
              <h3 className="text-xl font-bold text-indigo-800 mb-2">
                Forfait Revue de conformité CCMI — 500 € HT
              </h3>
              <p className="text-sm text-slate-500 mb-4">Soit 600 € TTC (TVA 20%)</p>
              <ul className="space-y-2 text-sm text-slate-700 mb-4">
                <li>• Revue du projet CCMI</li>
                <li>• Contrôle des garde-fous</li>
                <li>• Lecture de l&apos;analyse IA</li>
                <li>• Décision humaine : acceptable / à corriger / prêt pour validation juridique</li>
                <li>• Commentaire du juriste</li>
                <li>• Génération du rapport de revue PDF</li>
              </ul>
              <div className="bg-amber-50 border border-amber-200 rounded p-3 space-y-1">
                <p className="text-xs text-amber-800 italic">⚠ Revue documentaire d&apos;aide à la conformité — ne constitue pas un avis juridique définitif.</p>
                <p className="text-xs text-amber-800 italic">⚠ Pour une consultation juridique formelle, contactez un avocat ou un professionnel habilité.</p>
                <p className="text-xs text-amber-800 italic">⚠ Aucun changement de statut CCMI sans décision humaine explicite.</p>
              </div>
            </div>
            <div className="bg-indigo-900 text-white rounded-2xl shadow-md p-6 flex flex-col justify-between">
              <div>
                <p className="text-4xl font-extrabold">500 €</p>
                <p className="text-sm text-indigo-200">HT (one-shot)</p>
                <p className="text-xs text-indigo-300 mt-4">Paiement sécurisé Stripe.</p>
              </div>
              <a
                href="/admin/legalcoop"
                className="mt-6 inline-flex items-center justify-center bg-white text-indigo-900 font-semibold rounded-lg px-4 py-2 hover:bg-indigo-50 transition-colors text-sm"
                data-testid="legalcoop-cta-dispositifs"
              >
                Accéder au service LEGALCOOP
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* V37.74 VEFA — Tunnel programme immobilier */}
      <section className="py-16 bg-gradient-to-br from-amber-50 via-slate-50 to-amber-50" data-testid="dispositifs-vefa-section">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-slate-800 mb-2">
              VEFA — Programme immobilier
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Tunnel dédié à la Vente en l&apos;État Futur d&apos;Achèvement : contrat préliminaire
              de réservation, dossier notaire, appels de fonds plafonnés et contrôles de
              conformité légale (CCH L261-1 et s.).
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="md:col-span-2 bg-white rounded-2xl shadow-md p-6 border border-amber-200">
              <h3 className="text-xl font-bold text-amber-800 mb-2">
                Garde-fous VEFA spécifiques
              </h3>
              <ul className="space-y-2 text-sm text-slate-700 mb-4">
                <li>• GFA/GFR obligatoire avant signature de l&apos;acte</li>
                <li>• Dépôt de garantie plafonné : 5 % (≤ 12 mois), 2 % (≤ 24 mois), 0 % au-delà</li>
                <li>• Dépôt sur compte spécial ou notaire dépositaire</li>
                <li>• Appels de fonds cumulés : <strong>35 / 70 / 95 / 100</strong></li>
                <li>• Projet d&apos;acte notifié un mois avant signature</li>
                <li>• Plan coté et notice descriptive annexés</li>
                <li>• Travaux réservés limités à la liste autorisée</li>
              </ul>
              <div className="bg-amber-100 border border-amber-300 rounded p-3 space-y-1">
                <p className="text-xs text-amber-900 italic font-semibold">⚠ L&apos;acte définitif VEFA doit obligatoirement être reçu par notaire.</p>
                <p className="text-xs text-amber-900 italic">⚠ Le tunnel ne génère JAMAIS l&apos;acte authentique : uniquement contrat préliminaire, dossier notaire, appels de fonds, checklist conformité.</p>
                <p className="text-xs text-amber-900 italic">⚠ Les garde-fous VEFA sont distincts des règles CCMI.</p>
              </div>
            </div>
            <div className="bg-amber-900 text-white rounded-2xl shadow-md p-6 flex flex-col justify-between">
              <div>
                <p className="text-3xl font-extrabold">Tunnel VEFA</p>
                <p className="text-sm text-amber-200 mt-2">Contrat préliminaire + dossier notaire</p>
                <p className="text-xs text-amber-300 mt-4">CCH L261-1 et s., R261-13, R261-14.</p>
              </div>
              <a
                href="/simulateurs/vefa-tunnel"
                className="mt-6 inline-flex items-center justify-center bg-white text-amber-900 font-semibold rounded-lg px-4 py-2 hover:bg-amber-50 transition-colors text-sm"
                data-testid="vefa-cta-dispositifs"
              >
                Accéder au tunnel VEFA
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <Footer />
    </div>
  );
}